import * as React from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { useAuth } from "@/contexts/auth-context";
import { formatDate } from "@/lib/format-date";
import { useAdminUsers } from "@/hooks/queries/useAdminUsers";
import type { AdminUserRow, AdminUsersFilters } from "@/hooks/queries/admin-users-types";
import { toAdminUserError } from "@/routes/admin/admin-user-error";
import { ERASURE_RETENTION_STATEMENTS } from "@/routes/admin/erasure-retention-copy";
import {
  ACCESS_STATE_FILTER_OPTIONS,
  adminAccessStatePill,
  adminCalibrationPill,
  EMPTY_CELL,
  ROLE_FILTER_OPTIONS,
  roleLabel,
  subscriptionPill,
} from "@/routes/admin/admin-users-derivations";

// Screen 20 — the platform-admin user directory (Story 11.8, FR52-FR56).
//
// Two postures this file exists to hold:
//
//  • EVERY filter is server-side and lives in the URL search params, so a filtered
//    list is shareable and survives a reload. Nothing is filtered client-side —
//    an org_admin's scope is 11.1's WHERE-by-claim, and a client that narrowed a
//    response would make the isolation untestable from the outside.
//  • The client sends NO `org_id` for an org_admin. `?org_id=` is read from the URL
//    only (boundary #5), so a super_admin can deep-link to one org's slice and an
//    org_admin's client never chooses its own scope.
//
// The mockup's fourth "Subscription state" chip is deliberately NOT built: 11.1
// serves no `?subscription_state=`, and a control that silently does nothing is
// worse than an absent one. Recorded as a divergence with basis (AC2).

const HEADING_ID = "admin-users-heading";
const LIST_HEADING_ID = "admin-users-list-heading";

// Same window as the sibling audit console in this folder
// (`AuditLogFilterBar.ts:25` ACTOR_SEARCH_DEBOUNCE_MS) so the two admin search
// boxes behave identically.
export const USER_SEARCH_DEBOUNCE_MS = 300;

/**
 * Router state the sibling admin screens hand back on a successful write:
 * 11.9's create screen after an invite, 11.12's detail screen after an erasure.
 */
export interface AdminUsersListRouterState {
  invitedEmail?: string;
  /**
   * The erased user's display name, captured BEFORE the write. There is no id
   * here on purpose: the row survives erasure as a tombstone, so linking back to
   * it would offer a detail page with nothing left on it.
   */
  erasedName?: string;
}

/**
 * Router state this screen hands to the DETAIL screen, so its "Back to users"
 * can return the admin to the list they were actually reading (AC7).
 *
 * `listSearch` is the raw `URLSearchParams` string — every filter, the scope
 * deep-link and the page number, exactly as they stood. Carried in router state
 * rather than appended to the detail URL because it is not part of that route's
 * identity: two admins looking at the same user from different filtered lists
 * must share one cache key and one shareable link.
 */
export interface AdminUserDetailRouterState {
  listSearch?: string;
}

/** Read the URL search params into the filter object the query key is built from. */
export function filtersFromParams(params: URLSearchParams): AdminUsersFilters {
  const filters: AdminUsersFilters = {};
  const q = params.get("q");
  const role = params.get("role");
  const status = params.get("status");
  const orgId = params.get("org_id");
  const page = Number(params.get("page"));

  if (q) filters.q = q;
  if (role) filters.role = role;
  if (status) filters.status = status;
  if (orgId) filters.org_id = orgId;
  if (Number.isInteger(page) && page > 1) filters.page = page;
  return filters;
}

/**
 * True when a filter the USER chose is active. `org_id` is excluded on purpose:
 * it is a deep-link scope, not something a Clear-filters button should silently
 * drop out from under a super_admin who arrived from an org screen.
 */
export function hasUserFilters(filters: AdminUsersFilters): boolean {
  return Boolean(filters.q || filters.role || filters.status);
}

function fullName(row: AdminUserRow): string {
  return [row.first_name, row.last_name].filter(Boolean).join(" ") || row.email;
}

export function AdminUsersListScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasRole } = useAuth();
  const location = useLocation();

  const filters = React.useMemo(() => filtersFromParams(searchParams), [searchParams]);

  // The search box is uncontrolled by the URL between keystrokes: typing updates
  // local state immediately and the URL (and therefore the request) only after the
  // debounce, so a search does not fire one request per character.
  const [searchTerm, setSearchTerm] = React.useState(filters.q ?? "");
  const urlQ = filters.q ?? "";
  // Re-seed when the URL changes from OUTSIDE this input (Clear filters, a
  // back-navigation, a deep link) — never on our own debounced write, which has
  // already made the two equal by the time this runs.
  React.useEffect(() => {
    setSearchTerm((current) => (current === urlQ ? current : urlQ));
  }, [urlQ]);

  const setParam = React.useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          // Any filter change invalidates the current page: page 3 of an unfiltered
          // list is not page 3 of a filtered one, and holding it would show an
          // empty page for a search that has results.
          if (key !== "page") next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    if (searchTerm === urlQ) return;
    const timer = setTimeout(() => setParam("q", searchTerm.trim()), USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, urlQ, setParam]);

  const clearFilters = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of ["q", "role", "status", "page"]) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const { data, isPending, error } = useAdminUsers(filters);

  // Boundary #20 — the CTA is super_admin only. 11.2 denies `invite_platform_user`
  // to an org_admin, whose instrument is /org-admin/add-person instead; linking to
  // a control that 403s is the posture `nav-config.ts:52-57` already refuses.
  const canCreateUser = hasRole("super_admin");

  const routerState = location.state as AdminUsersListRouterState | null;
  const invitedEmail = routerState?.invitedEmail;
  const erasedName = routerState?.erasedName;

  // What every row link hands to the detail screen so its "Back to users" can
  // return here rather than to page 1 of an unfiltered list (AC7). The raw
  // search string, so a filter added later travels without touching this.
  const detailState = React.useMemo<AdminUserDetailRouterState>(
    () => ({ listSearch: searchParams.toString() }),
    [searchParams],
  );
  const mapped = error ? toAdminUserError(error) : null;
  const page = filters.page ?? 1;

  // The backend serves five access states; boundary #9 exposes THREE as chips,
  // because that is what the client-validated Screen 20 carries. `?status=removed`
  // and `?status=erased` therefore stay reachable by hand-editing the URL — and a
  // `<select>` bound to a value no `<option>` carries renders BLANK, so the control
  // denied the filter it was in fact applying while the rows below it obeyed it.
  //
  // A TRANSIENT option, present only while such a value is in the URL, is the
  // narrow repair: the control names the filter it is applying, without adding a
  // fourth chip to a screen that has not been re-validated with one. Its label
  // comes from the same pill map the rows use, so the control and the rows cannot
  // disagree — and an unrecognised value inherits that map's fail-closed
  // "Unknown state" rather than being echoed raw into the UI.
  const unlistedStatus =
    filters.status && !ACCESS_STATE_FILTER_OPTIONS.some((o) => o.value === filters.status)
      ? filters.status
      : null;

  const statusOptions = React.useMemo(() => {
    const base = ACCESS_STATE_FILTER_OPTIONS.filter((o) => o.value !== "");
    if (!unlistedStatus) return base;
    return [...base, { value: unlistedStatus, label: adminAccessStatePill(unlistedStatus).label }];
  }, [unlistedStatus]);

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 id={HEADING_ID} className="display-xs text-[color:var(--text-primary)]">
            Users
          </h1>
          <p className="text-sm text-[color:var(--text-secondary)]">
            Review clinician and admin accounts, then open a user profile to inspect account
            state or take action.
          </p>
        </div>
        {canCreateUser ? (
          <Button asChild>
            <Link to="/admin/users/new">Create user</Link>
          </Button>
        ) : null}
      </div>

      {invitedEmail ? (
        <Banner variant="success" title="Invitation sent">
          <p>{`An invitation was emailed to ${invitedEmail}.`}</p>
        </Banner>
      ) : null}

      {/* Story 11.12 AC10 — the erasure confirmation. It restates the retentions
          the modal listed, because this is the screen the admin will be looking
          at when they go back to answer the data-subject request — and it
          restates them FROM THE SAME SOURCE the modal read. Written out
          separately, this banner had already fallen behind: it named two of the
          retentions where the modal named three, so the story got shorter the
          moment the button was pressed. */}
      {erasedName ? (
        <Banner variant="success" title="User deleted">
          <p>{`${erasedName}'s account and sign-in details have been erased.`}</p>
          <ul className="list-disc pl-5">
            {ERASURE_RETENTION_STATEMENTS.map((statement) => (
              <li key={statement.layer}>{statement.sentence}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <FormField label="Search user" htmlFor="admin-users-search">
          <Input
            id="admin-users-search"
            type="search"
            value={searchTerm}
            placeholder="Name or email"
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </FormField>
        <FormField label="Role" htmlFor="admin-users-role">
          <SearchableCombobox
            id="admin-users-role"
            options={ROLE_FILTER_OPTIONS.filter((o) => o.value !== "")}
            value={filters.role ?? ""}
            onChange={(value) => setParam("role", value)}
            placeholder="All roles"
            searchAriaLabel="Filter by role"
            showSearch={false}
          />
        </FormField>
        <FormField label="Access state" htmlFor="admin-users-status">
          <SearchableCombobox
            id="admin-users-status"
            options={statusOptions}
            value={filters.status ?? ""}
            onChange={(value) => setParam("status", value)}
            placeholder="All access states"
            searchAriaLabel="Filter by access state"
            showSearch={false}
          />
        </FormField>
      </div>

      {mapped?.banner ? (
        <Banner variant={mapped.banner.variant} title={mapped.banner.title}>
          <p>{mapped.banner.body}</p>
          {mapped.banner.code ? (
            <p className="text-sm">{`Reference code: ${mapped.banner.code}`}</p>
          ) : null}
        </Banner>
      ) : null}

      {/* Loading renders NOTHING — there is no skeleton primitive in components/ui,
          and the sibling admin screens take the same posture. */}
      {!isPending && !error && data ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2
              id={LIST_HEADING_ID}
              className="text-lg font-semibold text-[color:var(--text-primary)]"
            >
              User list
            </h2>
            <p className="text-sm text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
              {`${data.count} ${data.count === 1 ? "user" : "users"}`}
            </p>
          </div>

          {data.results.length === 0 ? (
            // The two empty states are DIFFERENT problems: one needs a different
            // search, the other needs the first user to exist. Telling them apart
            // is the whole value of this branch.
            hasUserFilters(filters) ? (
              <Banner variant="info" title="No users match these filters">
                <p>Try a different search term, or clear the filters to see every user.</p>
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              </Banner>
            ) : (
              <Banner variant="info" title="No users yet">
                <p>No user accounts exist in this view yet.</p>
              </Banner>
            )
          ) : (
            <>
              <div className="overflow-x-auto rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
                <table
                  className="w-full border-collapse text-left text-sm"
                  aria-labelledby={LIST_HEADING_ID}
                >
                  <thead>
                    <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
                      <th scope="col" className="px-4 py-3 font-semibold">
                        User
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Role
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Subscription
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Renewal
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Calibration
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Access State
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map((row) => {
                      const subscription = subscriptionPill(row.subscription);
                      const calibration = adminCalibrationPill(row.calibration);
                      // The access-state column is the access-state discriminator.
                      // The mockup's `info "Admin"` value for an admin row is NOT
                      // built: overloading it with a role would render a BLOCKED
                      // super_admin as "Admin" — the most reassuring label available
                      // for the row most needing attention. The Role column already
                      // says Admin.
                      const access = adminAccessStatePill(row.access_state);
                      return (
                        <tr
                          key={row.user_id}
                          className="border-b border-[color:var(--bg-quaternary)] align-middle last:border-b-0"
                        >
                          <td className="px-4 py-3">
                            <Link
                              to={`/admin/users/${encodeURIComponent(row.user_id)}`}
                              state={detailState}
                              className="font-medium text-[color:var(--text-brand-secondary)] underline-offset-2 hover:underline"
                            >
                              {fullName(row)}
                            </Link>
                            <div className="text-xs text-[color:var(--text-tertiary)]">
                              {row.email}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[color:var(--text-secondary)]">
                            {roleLabel(row.role)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={subscription.variant}>{subscription.label}</Badge>
                          </td>
                          <td className="px-4 py-3 text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
                            {row.subscription.current_period_end
                              ? formatDate(row.subscription.current_period_end)
                              : EMPTY_CELL}
                          </td>
                          {/* tabular-nums: the "{N}/{M} cases" pill makes this a
                              numeric column. */}
                          <td className="px-4 py-3 [font-variant-numeric:tabular-nums]">
                            <Badge variant={calibration.variant}>{calibration.label}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={access.variant}>{access.label}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Page-based navigation, with the server's `previous`/`next` URLs as
                  the authority on whether a neighbouring page EXISTS. The URLs
                  themselves are not followed — every filter already lives in the
                  search params, so bumping `page` preserves all of them. */}
              {data.previous || data.next ? (
                <nav
                  className="flex items-center justify-between gap-4"
                  aria-label="User list pagination"
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!data.previous}
                    onClick={() => setParam("page", String(page - 1))}
                  >
                    Previous
                  </Button>
                  <p className="text-sm text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
                    {`Page ${page}`}
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!data.next}
                    onClick={() => setParam("page", String(page + 1))}
                  >
                    Next
                  </Button>
                </nav>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
